import React, { useState, useEffect } from 'react';
import {
  Card, Form, Select, InputNumber, Switch, Button,
  Typography, Space, Divider, Row, Col, Descriptions, Tag, Popconfirm,
  Alert, Skeleton, Table, Spin,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import {
  SettingOutlined, SaveOutlined, DeleteOutlined, InfoCircleOutlined,
  PercentageOutlined, DollarOutlined, ClockCircleOutlined,
  ThunderboltOutlined, EyeOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiService } from '../services/api';
import type { LateFeeRunResult, LateFeeRunResultItem } from '../types';
import { useCondoStore } from '../store/condoStore';
import type { LateFeePolicyCalculationType } from '../types';
import { currency } from '../utils/currency';   // Sprint 146 (R14-11)

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const CALC_TYPE_DESCRIPTIONS: Record<LateFeePolicyCalculationType, string> = {
  FLAT:        'A fixed dollar amount is added once when a fee becomes overdue (e.g., $25 per late payment).',
  PERCENTAGE:  'A percentage of the outstanding balance is charged monthly until paid (e.g., 1.5% per month).',
  COMPOUNDING: 'Interest compounds on the outstanding balance including previously accrued late fees.',
};

const LateFeePolicy: React.FC = () => {
  const {
    lateFeePolicy, fetchLateFeePolicy, upsertLateFeePolicy, deleteLateFeePolicy,
    currentCondo, isLoading,
  } = useCondoStore() as any;

  const [form] = Form.useForm();
  const [calcType, setCalcType] = useState<LateFeePolicyCalculationType>('FLAT');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [engineRunning, setEngineRunning] = useState(false);
  const [engineResult, setEngineResult] = useState<LateFeeRunResult | null>(null);

  useEffect(() => {
    if (currentCondo?.id) {
      fetchLateFeePolicy(currentCondo.id).then(() => setLoaded(true));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchLateFeePolicy is a stable zustand action
  }, [currentCondo?.id]);

  useEffect(() => {
    if (lateFeePolicy) {
      form.setFieldsValue({
        gracePeriodDays: lateFeePolicy.gracePeriodDays,
        calculationType: lateFeePolicy.calculationType,
        flatAmount: lateFeePolicy.flatAmount,
        percentageRate: lateFeePolicy.percentageRate,
        maxCap: lateFeePolicy.maxCap,
        isActive: lateFeePolicy.isActive,
      });
      setCalcType(lateFeePolicy.calculationType);
    } else if (loaded) {
      // No policy — set defaults
      form.setFieldsValue({
        gracePeriodDays: 5,
        calculationType: 'FLAT',
        flatAmount: 25,
        isActive: true,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `form` is a stable AntD Form instance; effect should re-run only when the policy/loaded state changes
  }, [lateFeePolicy, loaded]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await upsertLateFeePolicy(currentCondo?.id, {
        gracePeriodDays: values.gracePeriodDays,
        calculationType: values.calculationType,
        flatAmount: values.calculationType !== 'PERCENTAGE' ? values.flatAmount : undefined,
        percentageRate: values.calculationType !== 'FLAT' ? values.percentageRate : undefined,
        maxCap: values.maxCap ?? undefined,
        isActive: values.isActive,
      });
      staticApi.message.success('Late fee policy saved');
    } catch {
      // validation error
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await deleteLateFeePolicy(currentCondo?.id);
    form.setFieldsValue({ gracePeriodDays: 5, calculationType: 'FLAT', flatAmount: 25, isActive: true });
    setCalcType('FLAT');
    staticApi.message.success('Late fee policy removed');
  };

  const handlePreview = async () => {
    if (!currentCondo?.id) return;
    setEngineRunning(true);
    setEngineResult(null);
    try {
      const result = await apiService.previewLateFees(currentCondo.id);
      setEngineResult(result);
    } catch {
      staticApi.message.error('Preview failed — ensure the policy is saved and active');
    } finally {
      setEngineRunning(false);
    }
  };

  const handleRunNow = async () => {
    if (!currentCondo?.id) return;
    setEngineRunning(true);
    setEngineResult(null);
    try {
      const result = await apiService.runLateFees(currentCondo.id);
      setEngineResult(result);
      if (result.feesApplied > 0) {
        staticApi.message.success(`${result.feesApplied} late fee(s) applied — ${currency(result.totalAmountApplied)} total`);
      } else {
        staticApi.message.info('No new late fees to apply at this time');
      }
    } catch {
      staticApi.message.error('Run failed — ensure the policy is saved and active');
    } finally {
      setEngineRunning(false);
    }
  };

  if (!loaded && isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 760 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <SettingOutlined style={{ marginRight: 10, color: '#1890ff' }} />
          Late Fee Policy
        </Title>
        <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
          Configure automatic late fees applied to overdue maintenance and assessment charges.
          Fees are calculated based on the due date plus the grace period.
        </Paragraph>
      </div>

      {/* Current Status Banner */}
      {lateFeePolicy ? (
        <Alert
          type={lateFeePolicy.isActive ? 'success' : 'warning'}
          showIcon
          message={lateFeePolicy.isActive ? 'Policy is Active' : 'Policy is Disabled'}
          description={
            lateFeePolicy.isActive
              ? `Currently applying ${lateFeePolicy.calculationType === 'FLAT'
                  ? `$${lateFeePolicy.flatAmount} flat fee`
                  : `${lateFeePolicy.percentageRate}% per month`
                } after a ${lateFeePolicy.gracePeriodDays}-day grace period.`
              : 'No late fees are being applied. Enable the policy to start charging late fees.'
          }
          style={{ marginBottom: 20 }}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="No Policy Configured"
          description="No late fee policy has been set for this condominium. Configure one below to automatically apply late charges to overdue fees."
          style={{ marginBottom: 20 }}
        />
      )}

      {/* Policy Form */}
      <Card title="Policy Configuration">
        <Form form={form} layout="vertical">
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="gracePeriodDays"
                label={
                  <Space>
                    <ClockCircleOutlined />
                    Grace Period (days)
                  </Space>
                }
                rules={[{ required: true, message: 'Required' }]}
                tooltip="Number of days after the due date before a late fee is applied"
              >
                <InputNumber min={0} max={60} style={{ width: '100%' }} addonAfter="days" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isActive" label="Policy Status" valuePropName="checked">
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Disabled"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="calculationType"
            label="Calculation Method"
            rules={[{ required: true }]}
          >
            <Select onChange={(v) => setCalcType(v as LateFeePolicyCalculationType)}>
              <Option value="FLAT">
                <Space>
                  <DollarOutlined />
                  Flat Fee — fixed dollar amount per late payment
                </Space>
              </Option>
              <Option value="PERCENTAGE">
                <Space>
                  <PercentageOutlined />
                  Percentage — monthly % of outstanding balance
                </Space>
              </Option>
              <Option value="COMPOUNDING">
                <Space>
                  <PercentageOutlined />
                  Compounding — % applied to balance including prior late fees
                </Space>
              </Option>
            </Select>
          </Form.Item>

          {/* Calculation type description */}
          <Alert
            type="info"
            showIcon={false}
            message={CALC_TYPE_DESCRIPTIONS[calcType]}
            style={{ marginBottom: 20, background: '#f0f5ff', border: '1px solid #d0e0ff' }}
          />

          <Row gutter={24}>
            {(calcType === 'FLAT' || calcType === 'COMPOUNDING') && (
              <Col span={12}>
                <Form.Item
                  name="flatAmount"
                  label="Flat Fee Amount"
                  rules={calcType === 'FLAT' ? [{ required: true }] : []}
                  tooltip="Fixed dollar amount charged per late payment"
                >
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} addonBefore="$" />
                </Form.Item>
              </Col>
            )}
            {(calcType === 'PERCENTAGE' || calcType === 'COMPOUNDING') && (
              <Col span={12}>
                <Form.Item
                  name="percentageRate"
                  label="Monthly Percentage Rate"
                  rules={[{ required: true }]}
                  tooltip="Percentage of outstanding balance charged per month"
                >
                  <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} addonAfter="% / month" />
                </Form.Item>
              </Col>
            )}
            <Col span={12}>
              <Form.Item
                name="maxCap"
                label="Maximum Late Fee Cap (optional)"
                tooltip="The maximum total late fee that can be charged on a single outstanding balance"
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} addonBefore="$" placeholder="No cap" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              {lateFeePolicy ? 'Update Policy' : 'Save Policy'}
            </Button>
            {lateFeePolicy && (
              <Popconfirm
                title="Remove late fee policy?"
                description="Late fees will no longer be applied to any overdue balances."
                onConfirm={handleDelete}
                okText="Remove"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Remove Policy
                </Button>
              </Popconfirm>
            )}
          </Space>
        </Form>
      </Card>

      {/* Current Policy Summary (read-only) */}
      {lateFeePolicy && (
        <Card title="Current Policy Summary" style={{ marginTop: 24 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Grace Period">
              {lateFeePolicy.gracePeriodDays} days
            </Descriptions.Item>
            <Descriptions.Item label="Method">
              <Tag color="blue">{lateFeePolicy.calculationType}</Tag>
            </Descriptions.Item>
            {lateFeePolicy.flatAmount != null && (
              <Descriptions.Item label="Flat Amount">
                ${lateFeePolicy.flatAmount.toFixed(2)}
              </Descriptions.Item>
            )}
            {lateFeePolicy.percentageRate != null && (
              <Descriptions.Item label="Rate">
                {lateFeePolicy.percentageRate}% / month
              </Descriptions.Item>
            )}
            {lateFeePolicy.maxCap != null && (
              <Descriptions.Item label="Max Cap">
                ${lateFeePolicy.maxCap.toFixed(2)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Status">
              <Tag color={lateFeePolicy.isActive ? 'success' : 'default'}>
                {lateFeePolicy.isActive ? 'Active' : 'Disabled'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Engine Control Panel (S17) */}
      {lateFeePolicy?.isActive && (
        <Card
          title={
            <Space>
              <ThunderboltOutlined style={{ color: '#fa8c16' }} />
              Late Fee Engine
            </Space>
          }
          style={{ marginTop: 24 }}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Runs automatically every night at 01:00 AM
            </Text>
          }
        >
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Preview which overdue fees would be charged before running, or trigger an
            immediate run for this condominium. The nightly scheduler covers all condos
            automatically — use manual run only when needed.
          </Paragraph>

          <Space wrap style={{ marginBottom: engineResult ? 20 : 0 }}>
            <Button
              icon={<EyeOutlined />}
              onClick={handlePreview}
              loading={engineRunning}
              disabled={engineRunning}
            >
              Preview (Dry Run)
            </Button>
            <Popconfirm
              title="Apply late fees now?"
              description="This will immediately create late fee charges for all eligible overdue balances."
              onConfirm={handleRunNow}
              okText="Apply Now"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="primary"
                danger
                icon={<ThunderboltOutlined />}
                loading={engineRunning}
                disabled={engineRunning}
              >
                Run Now
              </Button>
            </Popconfirm>
          </Space>

          {engineRunning && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <Spin tip="Processing…" />
            </div>
          )}

          {engineResult && !engineRunning && (
            <>
              <Divider />
              <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
                <Col xs={12} sm={6}>
                  <Card size="small" bordered={false} style={{ background: engineResult.preview ? '#f0f5ff' : '#f6ffed' }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                      {engineResult.preview ? 'Would Apply' : 'Fees Applied'}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: engineResult.preview ? '#2f54eb' : '#52c41a' }}>
                      {engineResult.feesApplied}
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" bordered={false} style={{ background: '#fff7e6' }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Skipped</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#fa8c16' }}>
                      {engineResult.feesSkipped}
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={8}>
                  <Card size="small" bordered={false} style={{ background: '#f6ffed' }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total Amount</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#52c41a' }}>
                      ${engineResult.totalAmountApplied.toFixed(2)}
                    </div>
                  </Card>
                </Col>
              </Row>

              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
                {engineResult.preview ? '(Preview — no fees were saved)' : `Run completed at ${dayjs(engineResult.ranAt).format('MMM D, YYYY h:mm:ss A')}`}
              </Text>

              {engineResult.items.length > 0 && (
                <Table<LateFeeRunResultItem>
                  dataSource={engineResult.items}
                  rowKey="parentFeeId"
                  size="small"
                  pagination={{ pageSize: 10, showTotal: t => `${t} fees` }}
                  columns={[
                    {
                      title: 'Original Fee',
                      dataIndex: 'parentFeeDescription',
                      ellipsis: true,
                    },
                    {
                      title: 'Original Amount',
                      dataIndex: 'parentFeeAmount',
                      width: 130,
                      render: (v: number) => currency(v),   // Sprint 146 (R14-11)
                    },
                    {
                      title: 'Late Fee',
                      dataIndex: 'lateFeeAmount',
                      width: 100,
                      render: (v: number) => v > 0 ? <Text style={{ color: '#f5222d' }}>+${v.toFixed(2)}</Text> : '—',
                    },
                    {
                      title: 'Status',
                      dataIndex: 'reason',
                      width: 200,
                      render: (r: string) => (
                        <Tag color={r === 'applied' ? 'success' : 'default'} icon={r === 'applied' ? <CheckCircleOutlined /> : undefined}>
                          {r}
                        </Tag>
                      ),
                    },
                  ]}
                />
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default LateFeePolicy;
